// src/features/simulators/mobile-desk/components/MobileDeskConfigForm.tsx

import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  PersonSearch as PersonSearchIcon,
  VpnKey as VpnKeyIcon,
  DocumentScanner as DocumentScannerIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { ReservationNumberControl } from '../../shared';
import { GuestType, KeyDeliveryMethod } from '../../shared/types/journey.types';
import { DocumentScanMethod } from '../types';

export interface MobileDeskConfigValues {
  guestType: GuestType;
  keyDeliveryMethod: KeyDeliveryMethod;
  documentScanMethod: DocumentScanMethod;
  reservationNumber: string;
  autoGenerateReservation: boolean;
}

interface MobileDeskConfigFormProps {
  values: MobileDeskConfigValues;
  onChange: (values: MobileDeskConfigValues) => void;
}

export const MobileDeskConfigForm: React.FC<MobileDeskConfigFormProps> = ({
  values,
  onChange,
}) => {
  const translate = useTranslate();

  const update = (partial: Partial<MobileDeskConfigValues>) => {
    onChange({ ...values, ...partial });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <MyFormSection
          title={translate('resources.simulators.kiosk.guest_type.label')}
          icon={<PersonSearchIcon />}
          variant="outlined"
        >
          <FormControl fullWidth>
            <InputLabel>{translate('resources.simulators.kiosk.guest_type.label')}</InputLabel>
            <Select
              value={values.guestType}
              onChange={(e) => update({ guestType: e.target.value as GuestType })}
              label={translate('resources.simulators.kiosk.guest_type.label')}
              size="small"
            >
              <MenuItem value={GuestType.RESERVATION}>
                {translate('resources.simulators.kiosk.guest_type.reservation')}
              </MenuItem>
              <MenuItem value={GuestType.WALKING}>
                {translate('resources.simulators.kiosk.guest_type.walking')}
              </MenuItem>
            </Select>
          </FormControl>

          {values.guestType === GuestType.RESERVATION && (
            <ReservationNumberControl
              value={values.reservationNumber}
              autoGenerate={values.autoGenerateReservation}
              onValueChange={(value) => update({ reservationNumber: value })}
              onAutoGenerateChange={(autoGenerate) => update({ autoGenerateReservation: autoGenerate })}
              prefix="RES"
            />
          )}
        </MyFormSection>
      </Grid>

      <Grid item xs={12} md={6}>
        <MyFormSection
          title={translate('resources.simulators.kiosk.key_delivery.label')}
          icon={<VpnKeyIcon />}
          variant="outlined"
        >
          <FormControl fullWidth>
            <InputLabel>{translate('resources.simulators.kiosk.key_delivery.label')}</InputLabel>
            <Select
              value={values.keyDeliveryMethod}
              onChange={(e) => update({ keyDeliveryMethod: e.target.value as KeyDeliveryMethod })}
              label={translate('resources.simulators.kiosk.key_delivery.label')}
              size="small"
            >
              <MenuItem value={KeyDeliveryMethod.MAGNETIC_CARD}>
                {translate('resources.simulators.kiosk.key_delivery.magnetic_card')}
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.RFID_CARD}>
                {translate('resources.simulators.kiosk.key_delivery.rfid_card')}
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.CONVENTIONAL_KEY}>
                {translate('resources.simulators.kiosk.key_delivery.conventional_key')}
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.DIGITAL_PIN}>
                {translate('resources.simulators.kiosk.key_delivery.digital_pin')}
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.DIGITAL_MOBILE}>
                {translate('resources.simulators.kiosk.key_delivery.digital_mobile')}
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.QR_DISPENSER}>
                {translate('resources.simulators.kiosk.key_delivery.qr_dispenser')}
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.MANUAL}>
                {translate('resources.simulators.kiosk.key_delivery.manual')}
              </MenuItem>
            </Select>
          </FormControl>
        </MyFormSection>
      </Grid>

      <Grid item xs={12} md={6}>
        <MyFormSection
          title={translate('resources.simulators.mobile_desk.document_scan.label')}
          icon={<DocumentScannerIcon />}
          variant="outlined"
        >
          <FormControl fullWidth>
            <InputLabel>{translate('resources.simulators.mobile_desk.document_scan.label')}</InputLabel>
            <Select
              value={values.documentScanMethod}
              onChange={(e) => update({ documentScanMethod: e.target.value as DocumentScanMethod })}
              label={translate('resources.simulators.mobile_desk.document_scan.label')}
              size="small"
            >
              <MenuItem value={DocumentScanMethod.AUTOMATIC}>
                {translate('resources.simulators.mobile_desk.document_scan.auto')}
              </MenuItem>
              <MenuItem value={DocumentScanMethod.MANUAL}>
                {translate('resources.simulators.mobile_desk.document_scan.manual')}
              </MenuItem>
              <MenuItem value={DocumentScanMethod.PHOTO}>
                {translate('resources.simulators.mobile_desk.document_scan.photo')}
              </MenuItem>
            </Select>
          </FormControl>
        </MyFormSection>
      </Grid>
    </Grid>
  );
};
